import { type Failure, RequestFailed, malformed, readJson, request } from './request'
import type { LibraryDocument } from './library'
import type { Note } from './notes'
import type { Task } from './tasks'
import type { Flashcard } from './flashcards'
import type { Board } from './boards'
import type { Appointment } from './timetable'

/**
 * The subjects, as the service describes them.
 *
 * A subject is the bracket that says what something is *about*, beside the folder that says where it
 * lies. The two do not replace each other, and neither does the single link between two pieces of
 * content that comes later.
 *
 * Two things travel with every subject so that a list is one request rather than one per row: the
 * folders anchored to it, and the counts per kind.
 */

export interface Subject {
  readonly id: string
  readonly name: string
  readonly description: string
  /** The markup the description is written in, as a media type. */
  readonly descriptionMarkup: string
  /** When it was archived, or `null` while it is running. */
  readonly archivedAt: string | null
  /** Where whatever comes into being at this subject is filed, or `null`. */
  readonly rootFolder: FolderName | null
  /** The folders anchored to it. Everything in them and below them belongs to this subject. */
  readonly folders: readonly FolderName[]
  readonly counts: SubjectCounts
  readonly createdAt: string
  readonly updatedAt: string
}

export interface FolderName {
  readonly id: string
  readonly name: string
  /** The whole path, so two folders called `Klausuren` can be told apart. */
  readonly path: string
}

export interface SubjectCounts {
  readonly documents: number
  readonly notes: number
  readonly tasks: number
  readonly flashcards: number
  readonly boards: number
  readonly appointments: number
}

/**
 * One subject a piece of content belongs to, as it is shown at that piece of content.
 *
 * `inherited` is the field this exists for: only a direct belonging can be undone where it is shown.
 * An inherited one is a statement about a folder, and is taken back at that folder — or by moving the
 * content out of it.
 */
export interface SubjectOf {
  readonly id: string
  readonly name: string
  readonly inherited: boolean
}

/** What a subject can be put around, one by one. A folder is not one of these — it is the rule. */
export type FiledKind = 'document' | 'note' | 'task' | 'flashcard' | 'board'

export interface SubjectContent {
  readonly documents: readonly SubjectDocument[]
  readonly notes: readonly Note[]
  readonly tasks: readonly Task[]
  readonly flashcards: readonly Flashcard[]
  readonly boards: readonly Board[]
  readonly appointments: readonly Appointment[]
}

export interface SubjectDocument {
  readonly document: LibraryDocument
  /** Whether it is here through a folder rather than because somebody filed it. */
  readonly inherited: boolean
}

/** Whether it is out of the daily view. Everything about it is still there. */
export function isArchived(subject: Subject): boolean {
  return subject.archivedAt !== null
}

/** How much is under a subject altogether — what the list shows beside its name. */
export function totalOf(counts: SubjectCounts): number {
  return (
    counts.documents +
    counts.notes +
    counts.tasks +
    counts.flashcards +
    counts.boards +
    counts.appointments
  )
}

/**
 * A code a foreign system spells this subject with — `ZPR` in WebUntis.
 *
 * A code belongs to at most one subject, so tying it here is what decides where everything carrying
 * it is filed; a subject collects as many codes as it is called things.
 */
export interface SourceKey {
  /** The system it comes from, as that system names itself: `WebUntis`. */
  readonly source: string
  readonly key: string
  readonly anchoredAt: string
}

/** A code that occurs in taken-over content, and what there is to know before assigning it. */
export interface SourceKeyInUse {
  readonly source: string
  readonly key: string
  /** What the source spells that code out as. Empty where it does not. */
  readonly name: string
  /** How much content carries it — so the offer reads as a subject and not as three letters. */
  readonly count: number
  /** The subject it already belongs to, or `null` while it belongs to none. */
  readonly subjectId: string | null
}

/**
 * What changed when a code was tied or untied.
 *
 * `followed` is why this is not an empty answer: tying a code reaches backwards over a whole term,
 * and a screen that could not say "37 appointments now belong to this subject" would be a screen on
 * which something large happened silently.
 */
export interface SourceKeyChange {
  readonly key: SourceKey | null
  readonly followed: number
}

const SUBJECTS = '/api/subjects'

export async function fetchSubjects(
  includeArchived = false,
  signal?: AbortSignal,
): Promise<Subject[]> {
  const response = await request(`${SUBJECTS}?archived=${includeArchived ? 'true' : 'false'}`, {
    signal,
  })
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer did not describe a list of subjects.')
  }
  return payload as Subject[]
}

export async function fetchSubjectContent(
  id: string,
  signal?: AbortSignal,
): Promise<SubjectContent> {
  const response = await request(`${SUBJECTS}/${encodeURIComponent(id)}/content`, { signal })
  return readJson<SubjectContent>(response)
}

/** What this subject is called in the systems content is taken over from. */
export async function fetchSourceKeys(id: string, signal?: AbortSignal): Promise<SourceKey[]> {
  const response = await request(`${SUBJECTS}/${encodeURIComponent(id)}/source-keys`, { signal })
  return readJson<SourceKey[]>(response)
}

/** Every code that occurs in taken-over content, with the subject that already holds it. */
export async function fetchSourceKeysInUse(signal?: AbortSignal): Promise<SourceKeyInUse[]> {
  const response = await request(`${SUBJECTS}/source-keys`, { signal })
  return readJson<SourceKeyInUse[]>(response)
}

/** Ties a code to this subject, and applies it backwards over everything already taken over. */
export async function anchorSourceKey(
  id: string,
  source: string,
  key: string,
): Promise<SourceKeyChange> {
  return (await change(`${SUBJECTS}/${encodeURIComponent(id)}/source-keys`, 'PUT', {
    source,
    key,
  })) as SourceKeyChange
}

/** Unties one, and takes the subject off everything that had it only because of this code. */
export async function releaseSourceKey(
  id: string,
  source: string,
  key: string,
): Promise<SourceKeyChange> {
  const where = `source=${encodeURIComponent(source)}&key=${encodeURIComponent(key)}`
  return (await change(
    `${SUBJECTS}/${encodeURIComponent(id)}/source-keys?${where}`,
    'DELETE',
  )) as SourceKeyChange
}

export async function addSubject(name: string, description = ''): Promise<Subject> {
  return (await change(SUBJECTS, 'POST', { name, description })) as Subject
}

export async function changeSubject(
  id: string,
  name: string,
  description: string,
  descriptionMarkup: string,
): Promise<Subject> {
  return (await change(`${SUBJECTS}/${encodeURIComponent(id)}`, 'PUT', {
    name,
    description,
    descriptionMarkup,
  })) as Subject
}

/** Out of the daily view, and completely kept. Nothing about a subject is ever lost by archiving. */
export async function archiveSubject(id: string, archived: boolean): Promise<Subject> {
  const step = archived ? 'archive' : 'unarchive'
  return (await change(`${SUBJECTS}/${encodeURIComponent(id)}/${step}`, 'POST')) as Subject
}

/**
 * Merges another subject into this one, and removes it.
 *
 * The address is the subject that **stays** — the one somebody is looking at when they notice they
 * made the same subject twice.
 */
export async function mergeSubject(into: string, from: string): Promise<Subject> {
  return (await change(`${SUBJECTS}/${encodeURIComponent(into)}/merge`, 'POST', {
    from,
  })) as Subject
}

/** Removes a subject. Its belongings go with it; no piece of content is changed or deleted. */
export async function removeSubject(id: string): Promise<void> {
  await change(`${SUBJECTS}/${encodeURIComponent(id)}`, 'DELETE')
}

/** Files one piece of content under one subject. Saying it twice changes nothing. */
export async function fileUnderSubject(
  id: string,
  kind: FiledKind,
  contentId: string,
): Promise<void> {
  await change(`${SUBJECTS}/${encodeURIComponent(id)}/content`, 'PUT', { kind, id: contentId })
}

/** Takes one belonging away, and leaves every other subject of that content alone. */
export async function unfileFromSubject(
  id: string,
  kind: FiledKind,
  contentId: string,
): Promise<void> {
  const where = `${SUBJECTS}/${encodeURIComponent(id)}/content`
  await change(`${where}?kind=${kind}&id=${encodeURIComponent(contentId)}`, 'DELETE')
}

/**
 * Anchors a folder — the most effective single act in this area.
 *
 * Everything in that folder and below it belongs to the subject afterwards, and not one file is
 * touched.
 */
export async function anchorFolder(id: string, folderId: string): Promise<void> {
  await change(
    `${SUBJECTS}/${encodeURIComponent(id)}/folders/${encodeURIComponent(folderId)}`,
    'PUT',
  )
}

export async function unanchorFolder(id: string, folderId: string): Promise<void> {
  await change(
    `${SUBJECTS}/${encodeURIComponent(id)}/folders/${encodeURIComponent(folderId)}`,
    'DELETE',
  )
}

/** Says where whatever comes into being at this subject is filed, or takes that away with `null`. */
export async function setRootFolder(id: string, folderId: string | null): Promise<Subject> {
  return (await change(`${SUBJECTS}/${encodeURIComponent(id)}/root-folder`, 'PUT', {
    folderId,
  })) as Subject
}

/** The statuses that carry a code this side knows how to say out loud. */
const REFUSALS = [400, 404, 409, 413] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'no-such-subject': 'That subject is not there any more.',
  'no-such-folder-for-subject': 'That folder is not there any more.',
  'empty-subject-name': 'A subject has to have a name.',
  'subject-name-taken':
    'There is already a subject of that name. Rename this one, or merge the two together.',
  'cannot-merge-into-itself': 'A subject cannot be merged into itself.',
  'subject-name-too-long': 'That name is longer than a subject holds.',
  'subject-description-too-long': 'That description is longer than a subject holds.',
  'empty-source-key': 'A subject code from another system has to say something.',
}

async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : readJson<unknown>(response)
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  const failure: Failure = {
    kind: status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    retryable: status >= 500,
    code: problem?.code ?? null,
  }
  return new RequestFailed(failure)
}
